<template>
    <DCard>
        <DRow>
            <DCol>
                <DTable
                    :items="tickets"
                    :fields="fields"
                    :items-per-page="10"
                    pagination
                    column-filter
                    :pagination-props="{align: 'center'}"
                    sorter
                >
                    <template #id="{item}">
                        <td>
                            <a
                                v-if="item.ticketUrl !== null"
                                :href="item.ticketUrl"
                                target="_blank"
                            >
                                {{ item.id }}
                            </a>
                            <span v-else>
                                {{ item.id }}
                            </span>
                        </td>
                    </template>
                    <template #orderNumberText="{item}">
                        <td
                            v-if="item.orders !== null"
                        >
                            <div
                                v-for="(order, index) in item.orders"
                                :key="index"
                                class="d-inline"
                            >
                                <Link
                                    v-if="userPermissions.includes('order.view')"
                                    :href="$route('orders.details', {order: order.orderRouteId, client: order.orderRouteClient})"
                                >
                                    {{ order.orderNumberText }}
                                </Link>

                                <span
                                    v-else
                                >{{ order.orderNumberText }}</span>

                                <span v-if="index !== Object.keys(item.orders).length - 1">, </span>
                            </div>
                        </td>
                        <td
                            v-else
                        />
                    </template>

                    <template #contractId="{item}">
                        <td
                            v-if="item.contracts !== null"
                        >
                            <div

                                v-for="(contract, index) in item.contracts"
                                :key="index"
                                class="d-inline"
                            >
                                <Link
                                    v-if="userPermissions.includes('contract.view')"
                                    :href="$route('customers.customers.contracts.show', { customer: customer.id, contract: contract })"
                                >
                                    {{ contract }}
                                </Link>

                                <span
                                    v-else
                                >{{ contract }}</span>

                                <span v-if="index !== Object.keys(item.contracts).length - 1">, </span>
                            </div>
                        </td>
                        <td
                            v-else
                        />
                    </template>

                    <template #invoiceId="{item}">
                        <td
                            v-if="item.invoices !== null"
                        >
                            <div
                                v-for="(invoice, index) in item.invoices"
                                :key="index"
                                class="d-inline"
                            >
                                <Link
                                    v-if="userPermissions.includes('invoice.view')"
                                    :href="$route('customers.customers.invoices.show', { customer: customer.id, invoice: invoice })"
                                >
                                    {{ invoice }}
                                </Link>

                                <span
                                    v-else
                                >{{ invoice }}</span>

                                <span v-if="index !== Object.keys(item.invoices).length - 1">, </span>
                            </div>
                        </td>
                        <td
                            v-else
                        />
                    </template>
                    <template #incident="{item}">
                        <td>
                            <IncidentFlag
                                v-if="userPermissions.includes('customer.incidents_view') && item.haveIncident"
                                id="tickets-incident-flag"
                                :active-incident="item.haveOpenIncident"
                                :relation-type="'tickets'"
                                :relation-id="item.id"
                                :customer-id="item.customerId"
                            />
                        </td>
                    </template>
                    <template #created_at="{item}">
                        <td>
                            {{ setDateFormat(item.created_at) }}
                        </td>
                    </template>

                    <template #updated_at="{item}">
                        <td>
                            {{ setDateFormat(item.updated_at) }}
                        </td>
                    </template>
                    <template #actions="{item}">
                        <td>
                            <DDropdown
                                v-if="item.haveIncident === false && userPermissions.includes('customer.incidents_manage')"
                                :id="`actions_dropdown_${item.id}`"
                                color="secondary"
                                add-menu-classes="pt-0 pb-0"
                                :toggler-text="__('messages.actions.actions')"
                                :size="'sm'"
                            >
                                <DDropdownItem
                                    v-if="item.haveIncident === false && userPermissions.includes('customer.incidents_manage')"
                                    :id="`open_new_incident_${item.id}`"
                                    @click="openNewIncident(item)"
                                >
                                    <div style="display: flex; align-items: center; width: 100%;">
                                        <DIcon
                                            name="plus"
                                            :size="17"
                                            class="pr-1"
                                        />
                                        <div>
                                            {{ __('customers.tickets.table.open_new_incident') }}
                                        </div>
                                    </div>
                                </DDropdownItem>
                            </DDropdown>
                        </td>
                    </template>
                </DTable>
            </DCol>
        </DRow>
        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
        </DFabs>
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
    </DCard>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'

export default {
    name: 'CustomerTicketsIndex',
    components: {
        CreateCloudPitCustomerModal,
        SSOFab,
        IncidentFlag
    },
    layout: [App, CustomerBase],
    props: {
        tickets: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            fields: [
                { key: 'id', label: this.__('customers.tickets.table.ticket_id'), _style: { width: '6%' }, filter: false },
                { key: 'subject', label: this.__('customers.tickets.table.subject'), _style: { width: '16%' }, filter: false },
                { key: 'requester', label: this.__('customers.tickets.table.requester'), _style: { width: '9%' }, filter: false },
                { key: 'incident', label: this.__('customers.tickets.table.incident'), _style: { width: '13%' }, filter: false },
                { key: 'status', label: this.__('customers.tickets.table.status'), _style: { width: '6%' }, filter: false },
                { key: 'orderNumberText', label: this.__('customers.tickets.table.order_number'), _style: { width: '13%' } },
                { key: 'contractId', label: this.__('customers.tickets.table.contract_id'), _style: { width: '13%' } },
                { key: 'invoiceId', label: this.__('customers.tickets.table.invoice_id'), _style: { width: '10%' } },
                { key: 'created_at', label: this.__('customers.tickets.table.created_at'), _style: { width: '9%' }, filter: false },
                { key: 'updated_at', label: this.__('customers.tickets.table.updated_at'), _style: { width: '9%' }, filter: false },
                { key: 'actions', label: '', _style: { width: '9%' }, filter: false }
            ],
            showCreateCloudpitCustomerModal: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        openNewIncident (item) {
            const orders = []

            if (item.orders !== null) {
                item.orders.forEach(item => {
                    orders.push(item.id)
                })
            }

            this.$inertia.post(this.$route('customers.customers.open-new-conversation-incident'), {
                customerId: this.customer.id,
                ticketId: item.id,
                contracts: item.contracts,
                invoices: item.invoices,
                orders: orders
            })
        }
    }
}
</script>
